import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface ModuleSection {
  title: string;
  content: string[];
  illustrationText?: string;
  additionalSection?: {
    title: string;
    content: string[];
  };
  additionalSection2?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: 'Understanding the Emotio-Psycho-Rational Self',
    content: [
      '<strong>Overview:</strong> This first module introduces the idea that your emotional, psychological, and rational systems work together to shape your well-being.',
      '<br/>',
      '<strong>Key Topics:</strong>',
      '• What "Emotio-Psycho-Rational" means',
      '• The connection between emotions, thoughts, and behaviors',
      '• The Mental Wellness Triad (Feel–Think–Act)',
      '<br/>',
      '<strong>Discussion:</strong> Emotions help us experience life; thoughts help us make sense of it; rationality guides how we respond. When one of these is unbalanced (for example, emotions overwhelming logic), stress and conflict arise.'
    ]
  },
  {
    title: 'What "Emotio-Psycho-Rational" Means',
    content: [
      'The term "Emotio-Psycho-Rational" refers to the three interconnected systems that shape your mental and emotional well-being:',
      '<br/>',
      '• <strong>Emotional (Emotio):</strong> Your feelings and affective responses to experiences. Emotions are the raw, instinctive reactions that color your perception of the world.',
      '• <strong>Psychological (Psycho):</strong> Your thought patterns, beliefs, and cognitive processes. This includes how you interpret events, process information, and maintain mental health.',
      '• <strong>Rational (Rational):</strong> Your logical thinking, decision-making, and problem-solving abilities. This is your capacity for clear, objective reasoning and wise action.',
      '• <strong>The Integration:</strong> These three systems work together in a dynamic balance. When they are aligned, you experience mental clarity, emotional stability, and purposeful action.'
    ]
  },
  {
    title: 'The Connection Between Emotions, Thoughts, and Behaviors',
    content: [
      'Your emotions, thoughts, and behaviors form an interconnected system where each influences the others:',
      '<br/>',
      '<strong>The Flow:</strong> Situation → Thoughts → Emotions → Behaviors → Results',
      '<br/>',
      '<strong>Example:</strong> You receive critical feedback at work (situation) → "I\'m not good enough" (thought) → Anxiety and self-doubt (emotion) → Avoid taking risks or procrastinate (behavior) → Poor performance results.',
      '<br/>',
      '<strong>The Power of Awareness:</strong> By understanding this connection, you can intervene at any point. Change a thought, and you change the emotional response. Modify a behavior, and you alter the outcome.',
      '<br/>',
      '<strong>Key Insight:</strong> You are not your thoughts or emotions–you are the observer who can choose how to respond.'
    ]
  },
  {
    title: 'The Mental Wellness Triad (Feel–Think–Act)',
    content: [
      'The Mental Wellness Triad represents the three essential components of psychological health:',
      '• <strong>Feel (Emotional Awareness):</strong> The ability to recognize, name, and understand your emotions without judgment. This includes emotional intelligence and self-awareness.',
      '• <strong>Think (Cognitive Clarity):</strong> The capacity for clear, balanced thinking. This involves challenging distorted thoughts, maintaining perspective, and logical reasoning.',
      '• <strong>Act (Behavioral Wisdom):</strong> The skill of responding thoughtfully rather than reacting impulsively. This includes decision-making, self-control, and purposeful action.',
      '• <strong>Balance is Key:</strong> When all three are in harmony, you experience mental resilience. Imbalance in any area can lead to stress, anxiety, or poor decision-making.'
    ],
    additionalSection: {
      title: 'Reflection Activity',
      content: [
        'Describe a recent situation where your emotions led you to act before thinking.',
        '• What was the situation?',
        '• What emotions did you feel?',
        '• What thoughts were running through your mind?',
        '• How did you behave?',
        '• What was the result?',
        '• What would you do differently now?',
        '<br/>',
        '<strong>Takeaway:</strong> Self-awareness is the first step toward emotional mastery. By observing the Feel-Think-Act cycle, you gain the power to choose your responses rather than being driven by unconscious patterns.'
      ]
    }
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      'You\'ve completed Module 1: Understanding the Emotio-Psycho-Rational Self! Now, let\'s reinforce your learning with a quick quiz to check your understanding.'
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your knowledge about the Emotio-Psycho-Rational framework.',
        'The quiz will help you review key concepts about emotions, thoughts, behaviors, and the Mental Wellness Triad.'
      ]
    }
  }
];

const EmotioPsychoRationalModule1Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('emotioPsychoRationalM1');
  const totalSteps = moduleContent.length;

  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, false);
    } else {
      await saveProgress(currentStep, true);
      navigate('/panel/learn/emotio-psycho-rational-module-1/quiz');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/emotio-psycho-rational-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        <div 
          className="module-left emotio-psycho-rational-panel"
        >
          <div className="module-header">
            <h1>Module 1</h1>
            <p className="module-subtitle">Understanding the Emotio-Psycho-Rational Self</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Learn about the interconnected systems of emotions, thoughts, and rational thinking that shape your mental well-being. Discover the Mental Wellness Triad and how self-awareness leads to emotional mastery.
            </p>
          </div>
        </div>

        <div className="module-right">
          <div className="module-content"> 
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title"> 
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection2 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection2.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection2.content.map((paragraph: string, index: number) => (
                    <p key={`additional2-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false} 
            >
              {currentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>
            
            {showCompletionMessage ? (
              <button
                className="btn-primary"
                onClick={() => navigate('/panel/learn/emotio-psycho-rational-module-1/quiz')}
              >
                Take the Quiz
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmotioPsychoRationalModule1Page;


